import { useCallback, useRef, forwardRef, memo, useImperativeHandle } from 'react';
import Cropper, { ReactCropperElement } from 'react-cropper';
import { useAppSelector } from '@/store/hooks/redux';
import { selectSingleCreativeMedia } from '@/editor/store/selectors/creative.selectors';
import { AvailablePlatformsEnum } from '@/editor/shared/constants/available-platforms';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import useImageCropper from '@/editor/shared/components/modals/hooks/useCropper';

export interface GooglePreviewRef {
  setAspectRatio: (ratio: number) => void;
  setCropBoxData: (data: Cropper.CropBoxData) => void;
  getCropBoxData: () => Cropper.CropBoxData | undefined;
  getCroppedBlob: () => Promise<Blob | null>;
}

interface GooglePreviewProps {
  image: { url: string; width: number };
  onCropChange: (cropData: any) => void;
}

const GooglePreview = memo(
  forwardRef<GooglePreviewRef, GooglePreviewProps>((props, ref) => {
    const { image, onCropChange } = props;
    const cropperRef = useRef<ReactCropperElement>(null);

    const creativeMedia = useAppSelector((state) =>
      selectSingleCreativeMedia(state, AvailablePlatformsEnum.GOOGLE_ADS)
    );

    const initialAspectRatio = 1.91;

    const {
      cropperLoading,
      setCropperLoading,
      imageBlob,
      setImageBlob,
      modalState,
      croppingInProgress,
      setCroppingInProgress,
    } = useImageCropper(creativeMedia);

    useImperativeHandle(ref, () => ({
      setAspectRatio: (ratio) => {
        cropperRef.current?.cropper?.setAspectRatio(ratio);
      },
      setCropBoxData: (data) => {
        cropperRef.current?.cropper.setCropBoxData(data);
      },
      getCropBoxData: () => {
        return cropperRef.current?.cropper.getCropBoxData();
      },
      getCroppedBlob: () => {
        return new Promise((resolve) => {
          const cropperInstance = cropperRef.current?.cropper;
          if (!cropperInstance) {
            resolve(null);
            return;
          }

          const canvas = cropperInstance.getCroppedCanvas();

          canvas.toBlob(
            (blob) => {
              resolve(blob);
            },
            'image/jpeg',
            0.9
          );
        });
      },
    }));

    const handleCrop = useCallback(() => {
      if (cropperRef.current) {
        onCropChange(cropperRef.current?.cropper.getCropBoxData());
      }
    }, [onCropChange]);

    const handleCropChange = useCallback(() => {
      const cropperInstance = cropperRef.current?.cropper;
      if (cropperInstance) {
        // console.log(cropperInstance.getCropBoxData());
      }
    }, []);

    if (!image) {
      return <Typography>Select an image</Typography>;
    }

    return (
      <Box sx={{ width: '100%' }}>
        <Cropper
          dragMode="none"
          draggable={false}
          src={imageBlob!}
          // crop={handleCropChange}
          style={{ width: '100%', minWidth: '300px' }}
          aspectRatio={initialAspectRatio}
          responsive={true}
          minCropBoxWidth={500}
          minCropBoxHeight={500}
          guides={false}
          scalable={false}
          zoomOnWheel={false}
          zoomOnTouch={false}
          zoomable={true}
          cropend={handleCrop}
          ref={cropperRef}
        />
      </Box>
    );
  })
);

export default GooglePreview;
